import React from "react";

import Grid from "@material-ui/core/Grid";
import { getIn, useField, useFormikContext } from "formik";

import { Input, Switch, withNamespace } from "components/Form/FormikControls";

import { Button, SecondaryButton } from "components/Form";
import { AccountsRecipientIcon } from "components/Icons/AccountsRecipientIcon";
import { CommercialEmailRecipientIcon } from "components/Icons/CommercialEmailRecipientIcon";
import { ComplianceEmailRecipientIcon } from "components/Icons/ComplianceEmailRecipientIcon";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/MaterialDialog";

interface EmailRecipientFormProps {
  namespace: string;
}

function EmailRecipientFormComponent(
  props: EmailRecipientFormProps,
): React.JSX.Element {
  const { namespace: ns } = props;
  const id = useField(withNamespace(ns, "id"))[0].value;
  const isCommercialReportRecipientField = useField(
    withNamespace(ns, "isCommercialReportRecipient"),
  );
  const isComplianceReportRecipientField = useField(
    withNamespace(ns, "isComplianceReportRecipient"),
  );

  function onAfterEmailChanged() {
    if (!id) {
      // when this is a new EmailRecipient

      if (typeof isCommercialReportRecipientField[0].value !== "boolean") {
        // When the field has not yet been set (boolean once set by user, otherwise undefined)
        isCommercialReportRecipientField[2].setValue(true);
      }

      if (typeof isComplianceReportRecipientField[0].value !== "boolean") {
        // When the field has not yet been set (boolean once set by user, otherwise undefined)
        isComplianceReportRecipientField[2].setValue(true);
      }
    }
  }

  return (
    <>
      <input
        name={withNamespace(ns, "id")}
        type="hidden"
        value={id || undefined}
      />
      <Grid item xs={12} sm={6}>
        <Input
          label="First Name"
          name={withNamespace(ns, "firstName")}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Input
          label="Last Name"
          name={withNamespace(ns, "lastName")}
          required
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Input
          label="Email"
          name={withNamespace(ns, "email")}
          onChangeExtra={onAfterEmailChanged}
          required
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <Input label="Phone" name={withNamespace(ns, "phoneNumber")} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Switch
          label={
            <>
              Is Accounts Recipient&nbsp;
              <AccountsRecipientIcon color="primary" fontSize="delta" />
            </>
          }
          name={withNamespace(ns, "isAccountsRecipient")}
          tooltip="Contacts receive documents generated through the Billing process. (Eg Invoice, RCTI, etc)"
          alignCenter
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Switch
          label={
            <>
              Is Commercial Recipient&nbsp;
              <CommercialEmailRecipientIcon color="primary" fontSize="delta" />
            </>
          }
          name={withNamespace(ns, "isCommercialReportRecipient")}
          tooltip="Contacts receive price sensitive saleyard reports. (Eg Buyer Reconciliations, Purchaser Details, etc)"
          alignCenter
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Switch
          label={
            <>
              Is Compliance Recipient&nbsp;
              <ComplianceEmailRecipientIcon color="primary" fontSize="delta" />
            </>
          }
          name={withNamespace(ns, "isComplianceReportRecipient")}
          tooltip="Contacts receive non-price sensitive saleyard reports. (Eg Post Sale Summary, RFID Reports, etc)"
          alignCenter
        />
      </Grid>
    </>
  );
}
interface EmailRecipientFormSectionProps {
  namespace: string;
  onSetPrimaryContactEmail: (email: string) => void;
}

export function EmailRecipientFormSection(
  props: EmailRecipientFormSectionProps,
) {
  const { namespace: ns, onSetPrimaryContactEmail } = props;

  const formikContext = useFormikContext();
  const { dirty, isValid, values, setFieldValue, setTouched } = formikContext;

  const emailRecipients = getIn(values, withNamespace(ns, "values")) as
    | EmailRecipient[]
    | undefined;

  const editingEmailRecipient = getIn(
    values,
    withNamespace(ns, "editingValue"),
  ) as EmailRecipient | null | undefined;

  const emailRecipientsCount = emailRecipients?.length || 0;

  function onClickClearEmailRecipientChanges(): void {
    setFieldValue(withNamespace(ns, "editingValue"), null, true);
    setTouched({});
  }

  function onClickCommitEmailRecipientChanges(): boolean {
    if (!emailRecipients || !editingEmailRecipient) return false;
    const newState = emailRecipients.slice();
    const existingIndex = newState.findIndex(
      (deploymentEmailRecipient: EmailRecipient) =>
        deploymentEmailRecipient.id === editingEmailRecipient.id,
    );
    if (existingIndex === -1) {
      // When this is a new DeploymentEmailRecipient
      newState.splice(newState.length, 1, {
        ...editingEmailRecipient,
        // Add a temporary (offline) id to allow the front end to track the changes to this entry.
        // Once submitted, and a response is returned, this will have and actual database primary key in here.
        id: -Date.now(),
      });

      if (emailRecipientsCount === 0 && editingEmailRecipient.email) {
        // adding the first contact - make it primary
        onSetPrimaryContactEmail(editingEmailRecipient.email);
      }
    } else {
      // When the user is editing an existing EmailRecipient
      newState.splice(existingIndex, 1, editingEmailRecipient);

      if (emailRecipientsCount === 1 && editingEmailRecipient.email) {
        // edited the only contact - make it primary
        onSetPrimaryContactEmail(editingEmailRecipient.email);
      }
    }

    setFieldValue(withNamespace(ns, "values"), newState, false);
    setFieldValue(withNamespace(ns, "editingValue"), null, true);

    setTouched({});
    return false;
  }

  const isAddUpdateEnabled = editingEmailRecipient !== null && dirty && isValid;
  const isEditingExistingEmailRecipient =
    editingEmailRecipient &&
    Array.isArray(emailRecipients) &&
    emailRecipients.some(
      (emailRecipient: EmailRecipient) =>
        emailRecipient.id === editingEmailRecipient.id,
    );

  const isOpen = Boolean(editingEmailRecipient);

  return (
    <Dialog open={isOpen} onClose={onClickClearEmailRecipientChanges}>
      <DialogTitle onClose={onClickClearEmailRecipientChanges}>
        {isEditingExistingEmailRecipient ? "Update" : "Add"} Contact
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <EmailRecipientFormComponent
            namespace={withNamespace(ns, "editingValue")}
          />
        </Grid>
        <DialogActions>
          <SecondaryButton
            onClick={onClickClearEmailRecipientChanges}
            type="button"
          >
            Cancel
          </SecondaryButton>
          <Button
            data-tour="submit"
            onClick={onClickCommitEmailRecipientChanges}
            disabled={!isAddUpdateEnabled}
            type="button"
          >
            {isEditingExistingEmailRecipient ? "Update" : "Add"} Contact
          </Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
}
